import { createFileRoute, Link } from "@tanstack/react-router";
import { STATS_TEXT } from "@/lib/stats";
import { SiteLayout } from "@/components/site/Layout";
import { Picture } from "@/components/site/Picture";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { CTAButton } from "@/components/site/Button";
import { BRAND } from "@/lib/brand";
import { ArrowRight, Mail, Phone } from "lucide-react";
import agentsAz from "@/assets/agents-az.jpg";
import { ValuesHeading } from "@/components/site/ValuesSection";

export const Route = createFileRoute("/meet-the-team")({
  head: () => ({
    meta: [
      { title: "About KCN - Meet the Kristan Cole Network Leadership Team" },
      {
        name: "description",
        content:
          `Meet the leadership of the Kristan Cole Network, a Keller Williams Scottsdale based team spanning ${STATS_TEXT.markets} markets and built on grit, grace, growth, and give.`,
      },
      { property: "og:title", content: "About the Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "The leadership team behind a $1B+ real estate operation across AZ and AK.",
      },
      { property: "og:url", content: "https://joinkcn.com/meet-the-team" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/meet-the-team" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": "https://joinkcn.com/meet-the-team#aboutpage",
          url: "https://joinkcn.com/meet-the-team",
          name: "Meet the Kristan Cole Network Leadership Team",
          description:
            `The leadership team behind the Kristan Cole Network, a national real estate operation across ${STATS_TEXT.markets} markets nationwide.`,
          about: {
            "@type": "Organization",
            name: "Kristan Cole Network",
            url: "https://joinkcn.com/",
            founder: { "@type": "Person", name: "Kristan Cole" },
            description:
              `A national real estate expansion team across ${STATS_TEXT.markets} markets nationwide led by Kristan Cole, closing $1B+ in 2025 volume across 3,000+ transactions.`,
            areaServed: ["Arizona", "Alaska"],
            sameAs: [
              BRAND.socials.facebook,
              BRAND.socials.linkedin,
              BRAND.socials.youtube,
              BRAND.socials.instagram,
            ],
            employee: team.map((m) => ({
              "@type": "Person",
              name: m.name,
              jobTitle: m.title,
              worksFor: { "@type": "Organization", name: "Kristan Cole Network" },
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://joinkcn.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Meet the Team",
              item: "https://joinkcn.com/meet-the-team",
            },
          ],
        }),
      },
      faqSchema(MEET_TEAM_FAQS, "https://joinkcn.com/meet-the-team"),
    ],
  }),
  component: AboutKCN,
});

type Member = {
  name: string;
  title: string;
  phone?: string;
  email?: string;
  image?: string;
};

import kristanImg from "@/assets/team-optimized/kristan.webp";
import joeImg from "@/assets/team-optimized/joe.webp";
import laurieImg from "@/assets/team-optimized/laurie.webp";
import kristiImg from "@/assets/team-optimized/kristi.webp";
import sheilaImg from "@/assets/team-optimized/sheila.webp";
import shanaImg from "@/assets/team-optimized/shana.webp";
import { DirectAnswer, FAQSection, faqSchema } from "@/components/site/AnswerBlock";
import { MEET_TEAM_ANSWER, MEET_TEAM_FAQS } from "@/lib/aeo";

const team: Member[] = [
  {
    name: "Kristan Cole",
    title: "Founder & CEO",
    phone: "512-944-9940",
    email: "kristan@kristancole.com",
    image: kristanImg,
  },
  {
    name: "Joe Quattrucci",
    title: "Vice President",
    phone: "207-318-6644",
    email: "joe@thekristancolenetwork.com",
    image: joeImg,
  },
  {
    name: "Laurie Johnson",
    title: "Director of Operations",
    phone: "602-550-6888",
    email: "operations@thekristancolenetwork.com",
    image: laurieImg,
  },
  {
    name: "Kristi Mock",
    title: "Vice President of the Alaska Hub",
    phone: "907-841-6979",
    email: "kristi@kristimock.com",
    image: kristiImg,
  },
  {
    name: "Sheila Moseley",
    title: "Controller / Director of KHOH",
    phone: "907-741-2929",
    email: "controller@thekristancolenetwork.com",
    image: sheilaImg,
  },
  {
    name: "Shana Ackles",
    title: "Vice President of Growthward Consulting",
    phone: "907-841-6238",
    email: "shana@growthwardconsulting.com",
    image: shanaImg,
  },
];

function initials(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");
}

function AboutKCN() {
  return (
    <SiteLayout>
      {/* FULL-BLEED HERO */}
      <section className="relative isolate min-h-[85vh] flex items-end overflow-hidden">
        <Picture
          src={agentsAz}
          alt="Kristan Cole Network team at a luxury property"
          pictureClassName="absolute inset-0 block h-full w-full"
          className="h-full w-full object-cover"
          width={1600}
          height={1200}
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/15" />
        <div className="relative container-edge pb-14 md:pb-16 pt-36 w-full">
          <p className="eyebrow text-foreground/90 mb-8">About the Kristan Cole Network · A KW Network</p>
          <h1 className="headline-xl max-w-5xl">
            One network. {STATS_TEXT.markets} markets.
            <br />
            <span className="serif-italic text-foreground/85">Zero ceiling.</span>
          </h1>

          <p className="mt-8 lede max-w-2xl text-foreground/90">
            KCN is a national network of KW brokerages across {STATS_TEXT.markets} markets - 
            built on grit, grace, growth, and give, and led by an operator
            with 40+ years in the business.
          </p>
        </div>
      </section>

      {/* WHO WE ARE */}
      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Picture
              src={BRAND.profilePics[1]}
              alt="Kristan Cole Network team"
              className="w-full aspect-[4/5] object-cover"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionEyebrow>Who we are</SectionEyebrow>
            <h2 className="headline-lg">
              A real operation - 
              <br />
              <span className="serif-italic text-primary">not a logo.</span>
            </h2>
            <div className="mt-8 space-y-5 text-foreground/90 text-lg leading-relaxed">
              <p>
                The Kristan Cole Network is built around the COLE Method™ - a
                framework for taking agents from chaos to clarity and from
                producer to CEO. In 2025 alone the team closed more than{" "}
                <strong className="text-foreground">$1B in volume</strong>{" "}
                across <strong className="text-foreground">3,000+ transactions</strong>.
              </p>
              <p>
                But what makes KCN actually different isn't the numbers. It's
                the leadership team behind them - the operators, mentors, and
                coaches who make the method work in real life.
              </p>
            </div>
            <div className="mt-10">
              <Link
                to="/kristan"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-white hover:text-foreground/70 transition-colors"
              >
                Read Kristan's full story
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* MEET THE TEAM */}
      <Section tone="darker">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <SectionEyebrow>Meet the team</SectionEyebrow>
            <h2 className="headline-lg">
              The leaders behind
              <br />
              <span className="serif-italic text-primary">the method.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 self-end">
            <p className="text-foreground/90 text-lg leading-relaxed">
              Operators, coaches, and builders across {STATS_TEXT.markets} markets nationwide - the
              people who actually run the day-to-day of a $1B+ team.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {team.map((m) => (
            <article
              key={m.name}
              className="group bg-background p-8 md:p-10 flex flex-col"
            >
              <div className="aspect-square w-full bg-white mb-6 overflow-hidden relative">
                {m.image ? (
                  <img
                    src={m.image}
                    alt={`${m.name} - ${m.title}`}
                    className="absolute inset-0 h-full w-full object-contain object-top grayscale contrast-[1.05] transition-[filter] duration-500 ease-out group-hover:grayscale-0"
                    loading="lazy"
                  />
                ) : (
                  <span className="absolute inset-0 flex items-center justify-center font-display text-7xl text-primary/70">
                    {initials(m.name)}
                  </span>
                )}
              </div>
              <h3 className="font-display text-2xl">{m.name}</h3>
              <p className="mt-2 text-sm uppercase tracking-[0.2em] text-white/90">
                {m.title}
              </p>
              <div className="mt-6 space-y-2 text-base text-foreground/85">
                {m.phone ? (
                  <a
                    href={`tel:${m.phone.replace(/[^0-9+]/g, "")}`}
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {m.phone}
                  </a>
                ) : null}
                {m.email ? (
                  <a
                    href={`mailto:${m.email}`}
                    className="flex items-center gap-2 hover:text-primary transition-colors break-all"
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    {m.email}
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* WHAT WE STAND FOR */}
      <Section tone="cream">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow className="text-cream-foreground/70">What we stand for</SectionEyebrow>
            <p className="mt-2 text-sm uppercase tracking-[0.22em] text-primary mb-6">
              The vision this team is built around
            </p>
            <p className="font-display text-2xl md:text-3xl leading-snug text-cream-foreground">
              A drama-free organization of highly productive agents with
              <span className="serif-italic text-primary"> actual wealth opportunities </span>
              for those who want to be millionaires.
            </p>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-cream-foreground/85 text-lg leading-relaxed">
            <ValuesHeading className="headline-lg text-cream-foreground" />

            <p>
              Four words decide who thrives on this team. They're the filter we
              hire on, the language we coach in, and the standard we hold each
              other to.
            </p>
            <p>
              We're operators first. We believe a real estate business should
              fund your life, not consume it - and that success isn't success
              until it pours back out into family, community, and the agents
              coming up behind you.
            </p>
            <div className="pt-4">
              <CTAButton href="/kristan" variant="outline">
                Read Kristan's Story
              </CTAButton>
            </div>
          </div>
        </div>
      </Section>

      <DirectAnswer question={MEET_TEAM_ANSWER.question} answer={MEET_TEAM_ANSWER.answer} />

      <FAQSection
        faqs={MEET_TEAM_FAQS}
        eyebrow="Questions about KCN"
        heading="What agents ask about this team."
      />

      <CTABanner cta="roundtable" />
    </SiteLayout>
  );
}
